import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import Register from '../login/Register';
import { useSession } from '../../contexts/session-context';
import { HUB_REGISTER_PATH } from '../../routes/paths';
import {
    clearSsoReturnOutcome,
    getSsoReturnOutcome,
    type SsoOutcome
} from '../../services/google-sso';
import { postLoginDestination } from '../login/post-login-destination';
import { GoogleSignUpCompletion } from '../login/google-sign-up-completion';

/**
 * UX-806: a Google sign-up that just created the account (`created`) stays on this page for the
 * account-completion screen, which sends the user on itself. A `created` load is the only one that
 * shows it: the outcome is read off the return fragment, so a refresh or a later visit goes to the
 * dashboard like any other signed-in visitor.
 */
function showsCompletion(ssoOutcome: SsoOutcome | undefined): boolean {
    return ssoOutcome === 'created';
}

export default function HubRegisterPage() {
    const { authorize, status, isAuthenticated, user } = useSession();
    const redirected = useRef(false);
    // UX-803: the Google sign-up return, banked by `hub-app.tsx` before the router existed — only a
    // return that landed on THIS page (not, say, the login page's `no-account` that navigated here).
    // Read once into state and then cleared, exactly as `hub-login-page.tsx` does.
    const [ssoOutcome] = useState(() => getSsoReturnOutcome(HUB_REGISTER_PATH));

    useEffect(() => {
        clearSsoReturnOutcome();
    }, []);

    useEffect(() => {
        // Only check the session once. After a client-side navigation between hub pages it is
        // already resolved, and re-running `authorize` would flip `status` back to 'loading' and
        // swap the whole page for the bare spinner (a white flash).
        if (status !== 'idle') return;
        authorize();
    }, [authorize, status]);

    useEffect(() => {
        if (!isAuthenticated || redirected.current || showsCompletion(ssoOutcome)) return;
        redirected.current = true;
        // A `success` here is a sign-up that matched an existing account; it signs in as the hub
        // login page does (ROBO-481), so a suspended business's Owner lands on billing.
        window.location.replace(postLoginDestination(user));
    }, [isAuthenticated, ssoOutcome, user]);

    if (isAuthenticated && showsCompletion(ssoOutcome)) {
        return <GoogleSignUpCompletion user={user} />;
    }

    // `created` / `success` land here with their SessionKey already persisted, so this spinner
    // covers the authorize and the redirect — the form never flashes. If that authorize fails the
    // form renders with the outcome and shows the Google error.
    if (status === 'loading' || status === 'idle' || isAuthenticated) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
            >
                <CircularProgress color="inherit" size={32} sx={{ opacity: 0.25 }} />
            </Box>
        );
    }

    return <Register ssoOutcome={ssoOutcome} />;
}
